import { UserOutlined } from '@ant-design/icons'
import useFetchAnalyticsStatistics from '@hooks/useFetchAnalyticsStatistics'
import {
  ResponseUserRegistrationAnalyticsStatistics,
  ResponseUserVisitsSpendAnalyticsStatistics,
  UserKinds,
} from '@models/user/UserAnalyticsStatistics'
import {
  ResponseVenueAnalyticsStatistics,
  VenueKinds,
} from '@models/venue/VenueAnalyticsStatistics'
import { fetchAnalyticsStatistics } from '@services/AnalyticsStatistics'
import { currencyFormat } from '@utils/currencyFormatter'
import { durationFormatter } from '@utils/durationFormatter'
import { Col, Row, Typography } from 'antd'
import { ReactNode } from 'react'
import { AnalyticsStatistic } from './Statistic'

const { Text } = Typography

interface Props {
  entity: 'user' | 'venue'
  entityId?: number
  kind: UserKinds | VenueKinds
}

const statsConfigs: Record<
  UserKinds | VenueKinds,
  {
    entity: 'user' | 'venue'
    items: {
      title: ReactNode
      help?: ReactNode
      dataKey: string
      formatter?: (v: any) => string | number
      prefix?: ReactNode
      suffix?: ReactNode
      align?: 'left' | 'right' | 'center'
    }[]
  }
> = {
  'user-visit-spend': {
    entity: 'user',
    items: [
      { title: 'Total visits', dataKey: 'visits' },
      {
        title: 'Total spend',
        dataKey: 'spend',
        formatter: (v) => currencyFormat(v, true),
      },
      {
        title: 'Spend per visit',
        dataKey: 'spendPerVisits',
        formatter: (v) => currencyFormat(v, true),
      },
    ],
  },
  'venue-app-impression': {
    entity: 'venue',
    items: [
      {
        title: 'Average session duration',
        help: (
          <Text style={{ color: 'white' }}>
            The average duration between opening and closing the venue details
            page in the app. <br />
            This metrics has been implemented in version 3.12.2 of the mobile
            app released on November 10, 2025, meaning that data prior to this
            version will not be available.
          </Text>
        ),
        dataKey: 'impressionAvgSessionDuration',
        formatter: (
          v: ResponseVenueAnalyticsStatistics['impressionAvgSessionDuration']
        ) =>
          !v || v.length === 0
            ? '--'
            : (durationFormatter(
                v?.reduce(
                  (acc, curr) => acc + curr.avgSessionDurationSeconds,
                  0
                ) / (v?.length ?? 1)
              ) ?? 0),
      },
      {
        title: 'Click-through rate on "Book" button',
        help: (
          <Text style={{ color: 'white' }}>
            The percentage of impressions that resulted in a click on the "Book"
            button. <br />
            This metrics has been implemented in version 3.12.2 of the mobile
            app released on November 10, 2025, meaning that data prior to this
            version will not be available.
          </Text>
        ),
        dataKey: 'impressionClickRateBookButton',
        suffix: '%',
        formatter: (
          v: ResponseVenueAnalyticsStatistics['impressionClickRateBookButton']
        ) =>
          !v || v.length === 0
            ? '--'
            : (Math.ceil(
                v?.reduce((acc, curr) => acc + curr.clickRatePercent, 0) /
                  (v?.length ?? 1)
              ) *
                100) /
              100, // Round number to 2 decimal places
      },
    ],
  },
  'venue-app-booking-conversion': {
    entity: 'venue',
    items: [
      {
        title: 'Anticipation period',
        dataKey: 'anticipationPeriod',
        help: 'Average time between the reservation creation date and the reservation date.',
        formatter: (v) => {
          return durationFormatter(v * 60) ?? 0 // API returns minutes, convert to seconds
        },
      },
    ],
  },
  'venue-app-revenue': {
    entity: 'venue',
    items: [
      {
        title: 'Total revenue',
        dataKey: 'revenueTotal',
        formatter: (v) => currencyFormat(v, true),
      },
      {
        title: 'Average spend per reservation',
        dataKey: 'revenueAvgSpendPerReservation',
        formatter: (v) => currencyFormat(v, true),
      },
      {
        title: 'Average spend per person',
        dataKey: 'revenueAvgSpendPerPerson',
        formatter: (v) => currencyFormat(v, true),
      },
    ],
  },
  'venue-app-reservations': {
    entity: 'venue',
    items: [{ title: 'Reservations', dataKey: 'totalReservations' }],
  },
  'user-registration': {
    entity: 'user',
    items: [
      {
        title: 'Registrations',
        dataKey: 'count',
        prefix: <UserOutlined />,
        align: 'left',
      },
    ],
  },
}

const RowStatistics = ({ entity, entityId, kind }: Props) => {
  const { data, isLoading, error } = useFetchAnalyticsStatistics<
    any,
    | ResponseUserVisitsSpendAnalyticsStatistics
    | ResponseUserRegistrationAnalyticsStatistics
    | ResponseVenueAnalyticsStatistics
  >({
    entityId,
    kind,
    entity,
    fetchFunction: fetchAnalyticsStatistics,
  })

  return (
    <>
      <Row gutter={[16, 16]}>
        {statsConfigs[kind].items.map((item, index) => {
          const spanSize = 24 / statsConfigs[kind].items.length
          return (
            <Col span={spanSize} key={index}>
              <AnalyticsStatistic
                title={item.title}
                help={item.help}
                value={
                  data &&
                  (item.formatter
                    ? item.formatter((data as any)[item.dataKey])
                    : (data as any)[item.dataKey])
                }
                suffix={item.suffix}
                isLoading={isLoading}
                prefix={item.prefix}
                align={item.align}
              />
            </Col>
          )
        })}
      </Row>
      {error && <Text type="danger">{error}</Text>}
    </>
  )
}

type RowStatisticsType = { RowStatistics: typeof RowStatistics }
export { RowStatistics, type RowStatisticsType }
